<template>
  <v-col cols="9">
    <v-card-text>
      <v-form ref="form" v-model="isValid" lazy-validation autocomplete="off">
        <v-text-field
          dense outlined
          v-model="localForm.username"
          :label="$t('profiles.operatorCredentials.usernameLabel')"
          :counter="48"
          maxlength="48"
          :rules="[rules.required, rules.noBlankSpaces]"
          @input="emitChange"
          autocomplete="off"
        />
        <v-text-field
          dense outlined
          v-model="localForm.password"
          :label="$t('profiles.operatorCredentials.passwordLabel')"
          type="text"
          class="password-field"
          :class="{ 'password-visible': showPassword }"
          :append-icon="showPassword ? icons.mdiEyeOff : icons.mdiEye"
          :counter="30"
          maxlength="30"
          spellcheck="false"
          autocomplete="off"
          :rules="[rules.required, rules.min5, rules.oneDigit, rules.oneUpperCase, rules.oneSpecialChar, rules.oneLowerCase]"
          @input="emitChange"
          @click:append="showPassword = !showPassword"
        />
        <small>{{ $t('profiles.operatorCredentials.hints.minLength') }}</small><br/>
        <small>{{ $t('profiles.operatorCredentials.hints.includeNumber') }}</small><br/>
        <small>{{ $t('profiles.operatorCredentials.hints.includeUppercase') }}</small><br/>
        <small>{{ $t('profiles.operatorCredentials.hints.includeSpecial') }}</small>
      </v-form>
    </v-card-text>
  </v-col>
</template>

<script>
import { mdiEye, mdiEyeOff } from '@mdi/js';

export default {
  name: 'OperatorCredentialsForm',
  props: { form: { type: Object, required: true } },
  data() {
    return {
      isValid: false,
      showPassword: false,
      icons: { mdiEye, mdiEyeOff },
      rules: {
        required: v => (!!v || v === false) ? true : this.$t('profiles.operatorCredentials.rules.required'),
        min5: v => (v && v.length >= 5) || this.$t('profiles.operatorCredentials.rules.minLength'),
        oneDigit: v => (/\d/.test(v || '')) || this.$t('profiles.operatorCredentials.rules.includeNumber'),
        oneLowerCase: v => (/[a-z]/.test(v || '')) || this.$t('profiles.operatorCredentials.rules.includeLowercase'),
        oneUpperCase: v => (/[A-Z]/.test(v || '')) || this.$t('profiles.operatorCredentials.rules.includeUppercase'),
        oneSpecialChar: v => (/[\W_]/.test(v || '')) || this.$t('profiles.operatorCredentials.rules.includeSpecial'),
        noBlankSpaces: v => (v && !/\s/.test(v)) || this.$t('profiles.operatorCredentials.rules.noBlankSpaces'),
      },
      localForm: {
        username: '',
        password: '',
      },
    };
  },
  mounted() {
    this.syncFormData();
    // Readonly trick — the only reliable way to block browser password managers
    // on Windows (Chrome/Edge ignore autocomplete="off" on password fields).
    // Inputs start as readonly so the browser has nothing to autofill;
    // readonly is removed the moment the user focuses the field.
    this.$nextTick(() => {
      this.$el.querySelectorAll('input').forEach(el => {
        el.setAttribute('readonly', '');
        el.setAttribute('data-1p-ignore', '');
        el.setAttribute('data-lpignore', 'true');
        el.addEventListener('focus', () => el.removeAttribute('readonly'), { once: true });
      });
    });
  },
  watch: {
    form: {
      handler(newVal) {
        if (newVal) {
          this.syncFormData();
        }
      },
      immediate: true,
      deep: true
    }
  },
  beforeDestroy() {
    // Clear inputs before component is removed — prevents Chrome/Edge save-password prompt on navigation
    this.localForm.username = '';
    this.localForm.password = '';
    if (this.$el) {
      this.$el.querySelectorAll('input').forEach(el => { el.value = ''; });
    }
  },
  methods: {
    syncFormData() {
      // Sync parent form data to local form
      if (this.form) {
        Object.keys(this.localForm).forEach(key => {
          if (this.form[key] !== undefined) {
            this.localForm[key] = this.form[key];
          }
        });
      }
    },
    validate() { return this.$refs.form.validate(); },
    emitChange() {
      this.$emit('update:form', { ...this.localForm });
    }
  },
};
</script>

<style scoped>
/* Mask password field — type="text" prevents Chrome from detecting a password field */
.password-field ::v-deep input {
  -webkit-text-security: disc;
}
.password-field.password-visible ::v-deep input {
  -webkit-text-security: none;
}
</style>
